import React, { useState } from 'react';
import moment from 'moment';
import createTrack from '../../../services/actions/tracker_actions';
import { track } from '../../../services/tracking';
import {
  BiddingInfoDetailTypeSubtype,
  BiddingInfoTypes,
} from '../../../store/modules/bidding_store/types';
import BiddingInfoUrls from '../bidding_info_urls';

interface BiddingInfoProps {
  info: BiddingInfoTypes;
  // eslint-disable-next-line react/require-default-props
  initiallyOpen?: boolean;
  // eslint-disable-next-line react/require-default-props
  trackEvents?: boolean;
}

const BiddingInfoItem: React.FC<BiddingInfoProps> = ({
  info,
  initiallyOpen = false,
  trackEvents = true,
}) => {
  const [view, setView] = useState<boolean>(initiallyOpen);
  const [messagesOpened, setMessagesOpened] = useState<boolean>(false);

  const typeNames: Record<number, string> = {
    1: 'Esclarecimento',
    2: 'Impugnação',
    3: 'Aviso',
    4: 'Recurso',
    5: 'Diligência',
  };

  const type_name = typeNames[info?.type_id] || '';

  const DILIGENCE_TYPE_ID = 5;
  const MESSAGE_SUBTYPE_ID = 2;
  const isDiligence = info?.type_id === DILIGENCE_TYPE_ID;
  const diligenceSubtypeLabels: Record<number, string> = {
    1: 'Diligência',
    2: '',
    4: 'Análise',
    5: 'Conclusão',
  };

  const subtypeLabel = (dts: BiddingInfoDetailTypeSubtype) =>
    isDiligence
      ? diligenceSubtypeLabels[dts?.subtype_id] ?? dts?.subtype?.subtype_name
      : dts?.subtype?.subtype_name;

  const allSubtypes = info?.detail_type_subtypes ?? [];
  const messageSubtypes = isDiligence
    ? allSubtypes.filter((dts) => dts?.subtype_id === MESSAGE_SUBTYPE_ID)
    : [];
  const mainSubtypes = isDiligence
    ? allSubtypes.filter((dts) => dts?.subtype_id !== MESSAGE_SUBTYPE_ID)
    : allSubtypes;

  // mapeia o tipo do vínculo para o evento do catálogo (power-feature, eixo P)
  const vinculoEvents: Record<number, string> = {
    1: 'vinculo.viu_esclarecimentos',
    2: 'vinculo.viu_impugnacoes',
    3: 'vinculo.viu_avisos',
    4: 'vinculo.viu_recursos',
    5: 'vinculo.viu_diligencias',
  };

  const handleClick = () => {
    if (!trackEvents) {
      setView(!view);
      return;
    }
    createTrack(`Vínculo - Clicou em ${type_name}`);
    // tracking próprio (catálogo): o botão é um toggle — "viu" só na abertura,
    // fechar o item não é uma nova visualização
    const eventName = vinculoEvents[info?.type_id];
    if (!view && eventName) track(eventName, { tipo: type_name });
    setView(!view);
  };

  const captalizeSituation = (situation: string) => {
    if (!situation) {
      return '';
    }
    return situation.toLowerCase().charAt(0).toUpperCase() + situation.toLowerCase().slice(1);
  };

  const renderSubtype = (dts: BiddingInfoDetailTypeSubtype) => (
    <React.Fragment key={dts?.id}>
      <div>
        <hr className="m-0 p-0" />
        <div className="card my-2 p-2">
          <div className="d-flex">
            {subtypeLabel(dts) && (
              <p className="font-weight-bold">{captalizeSituation(subtypeLabel(dts))}</p>
            )}
            <p className="ml-auto">{info?.message_date && moment(dts?.message_date).format('L')}</p>
          </div>

          <p>{captalizeSituation(dts?.description)}</p>
        </div>
      </div>
      <BiddingInfoUrls urls={dts?.urls} />
    </React.Fragment>
  );

  return (
    <div className="card mb-1">
      <div className="card-body p-1 border border-dark rounded">
        <div className="row d-flex align-items-center mx-1" key={info?.id}>
          <div className="col-md-2 text-left">
            {info?.message_date && moment(info?.message_date).format('L')}
          </div>
          <div className="col-md text-left">
            {info?.title.length > 100
              ? `${captalizeSituation(info?.title.substring(0, 80))}...`
              : captalizeSituation(info?.title)}
          </div>
          <div className="col-md-2">{captalizeSituation(info?.situation)}</div>
          <button
            type="button"
            className="btn btn-link text-decoration-none d-flex align-items-center gap-1 p-0"
            style={{ outline: 'none', boxShadow: 'none' }}
            onClick={handleClick}
          >
            Visualizar
            <i className="material-icons">{view ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}</i>
          </button>
        </div>
        {view && mainSubtypes.map(renderSubtype)}
        {view && messageSubtypes.length > 0 && (
          <div>
            <hr className="m-0 p-0" />
            <button
              type="button"
              className="btn btn-link text-decoration-none d-flex align-items-center gap-1 p-0 my-2 font-weight-bold"
              style={{ outline: 'none', boxShadow: 'none' }}
              onClick={() => setMessagesOpened(!messagesOpened)}
            >
              Mensagens ({messageSubtypes.length})
              <i className="material-icons">
                {messagesOpened ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
              </i>
            </button>
            {messagesOpened && messageSubtypes.map(renderSubtype)}
          </div>
        )}
      </div>
    </div>
  );
};

export default BiddingInfoItem;
